import { Box, Gleam, Icon, IconButton, ZillowLogo } from '@zillow/constellation-10';
import { IconMessageFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserListingConversationWebApiHandler_cachedConversationCounts } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import { getNavDisplays } from '../../utils/displays';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import AccountMenu from '../VerticalNavigation/AccountMenu';
import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoDesktop from '../Logo/LogoDesktop';
import NotificationCenter from '../NotificationCenter';

import type { Props as HeaderNavigationProps } from '../HeaderNavigation';
import type { User } from '../../types';

export type Props = Partial<HeaderNavigationProps>;

// ZRM-16458: the new IA top bar (logo + notifications + messages + account).
// These elements are relocated out of the left sidebar (VerticalNavigation
// suppresses them via its `topBar` prop) and into this 72px bar.
const TopNavBar = (props: Props) => {
  const { displays } = getNavDisplays(props);

  const {
    altLogo = true,
    brand = 'zillow',
    reactRouterLink: Link,
    userModel = {} as User,
    webSocketInstance = null,
    escapeAllLinks = false,
  } = props;

  const { loggedIn } = userModel;
  const { displayMainMessages, displayMainNotificationCenter } = displays;

  // ZRM-16458: unread messages badge on the top-bar messages icon (matches the bell).
  const { data: conversationCounts } = useRentalManagerUserListingConversationWebApiHandler_cachedConversationCounts({
    enabled: Boolean(loggedIn) && displayMainMessages,
    meta: {
      tags: ['messages-cached-conversation-counts'],
      context: { api: 'cachedConversationCounts', details: {} },
    },
  });
  const unreadMessageCount = conversationCounts?.unreadCount ?? 0;

  const useAbsolutePaths = !props.useRelativePaths;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, props.host).getAllPaths();
  const { propertiesPage, inboxPage } = paths;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      }
    }
  };

  return (
    <Box
      data-testid="TopNavBar"
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '72px',
        paddingInline: '400',
        borderBottom: '1px solid token(colors.border.muted)',
        backgroundColor: 'bg.neutral',
      }}
    >
      <AdaptiveAnchor
        path={propertiesPage}
        escape={escapeAllLinks}
        useAbsolutePaths={useAbsolutePaths}
        link={Link}
        title="Return to properties page"
      >
        <Box css={{ width: altLogo ? '200px' : '154px', height: 'auto' }}>
          {altLogo ? <LogoDesktop alternate={true} /> : <ZillowLogo showLegacyLogo={true} />}
        </Box>
      </AdaptiveAnchor>

      <Box css={{ display: 'flex', alignItems: 'center', gap: '400' }}>
        {loggedIn && displayMainNotificationCenter && (
          <NotificationCenter brand={brand} webSocketInstance={webSocketInstance} topBar={true} />
        )}

        {loggedIn && displayMainMessages && (
          <AdaptiveAnchor
            path={inboxPage}
            escape={true}
            useAbsolutePaths={useAbsolutePaths}
            link={Link}
            title={HeaderTitles.messages}
            onClick={() => handleLinkClick(HeaderTitles.messages)}
          >
            {/* icon-only button with gray (neutral/bare) hover; Gleam sits on the icon for the unread badge */}
            <IconButton asChild tone="neutral" emphasis="bare" shape="circle" title={HeaderTitles.messages}>
              <span>
                <Gleam.Root css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon render={<IconMessageFilled />} size="md" />
                  {unreadMessageCount > 0 && <Gleam.Label count={unreadMessageCount} maxCount={99} maxSuffix />}
                </Gleam.Root>
              </span>
            </IconButton>
          </AdaptiveAnchor>
        )}

        {loggedIn && <AccountMenu displays={displays} paths={paths} userModel={userModel} avatarOnly={true} />}
      </Box>
    </Box>
  );
};

export default TopNavBar;
